import { Button, ButtonGroup } from "@bokio/designsystem/components/Button";
import { ModalFooter } from "@bokio/designsystem/components/Modal";
import { BankLangFactory, GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";
import { formatMessage, formatNumberCurrency } from "@bokio/shared/utils/format";

import type { ReactNode } from "react";

import * as styles from "./signAndPayFooter.scss";

interface SignAndPayFooterProps {
	selectedPayments: number;
	disabled: boolean;
	onClose: () => void;
	multipleCurrencies: boolean;
	totalSum: number;
	onSignAndPayClick: () => void;
	style: "popover" | "modal";
	isLoading: boolean;
	hideSignButton?: boolean;
	gateContent?: ReactNode;
	noticeContent?: ReactNode;
}

export const SignAndPayFooter = ({
	selectedPayments,
	disabled,
	onClose,
	multipleCurrencies,
	totalSum,
	onSignAndPayClick,
	style,
	isLoading,
	hideSignButton,
	gateContent,
	noticeContent,
}: SignAndPayFooterProps) => {
	const bankLang = BankLangFactory();
	const generalLang = GeneralLangFactory();
	const { companyInfo } = useCompanyInfo();
	const domesticCurrency = companyInfo.CountryMetadata.Currency;
	const router = useRouter();
	const handleViewPaymentsClick = () => {
		onClose();
		const path = getRoute("bankPayments", { company: companyInfo.Id });
		router.redirect(path);
	};

	const renderContent = () => (
		<div className={styles.footerContent}>
			<div className={styles.totalSummaryContainer}>
				<div className={styles.totalItems}>
					{formatMessage(
						bankLang.SignAndPayModal_TotalSelectedPayments,
						selectedPayments,
						() => (selectedPayments > 1 ? generalLang.Menu_Articles : generalLang.Item),
						t => (multipleCurrencies ? t : null),
					)}
				</div>
				<div className={styles.totalSum} data-testid={"SignAndPay_Total"}>
					{`${multipleCurrencies ? "~" : ""}${formatNumberCurrency(totalSum, domesticCurrency)}`}
				</div>
			</div>
			{noticeContent}
			{gateContent ? (
				<div className={styles.gateContentWrapper}>{gateContent}</div>
			) : (
				<ButtonGroup direction="column">
					{!hideSignButton && (
						<Button
							className={styles.fixedSizeButton}
							icon="bankid-solid"
							onClick={onSignAndPayClick}
							testId="SignAndPayModal_Submit"
							disabled={disabled}
							loading={isLoading}
						>
							{bankLang.SignAndPayModalTitle}
						</Button>
					)}
					<Button
						className={styles.fixedSizeButton}
						appearance="secondary"
						onClick={handleViewPaymentsClick}
						testId="ViewPayments_Button"
					>
						{bankLang.SignAndPayModal_ViewPayments}
					</Button>
				</ButtonGroup>
			)}
		</div>
	);

	return style === "modal" ? (
		<ModalFooter>{renderContent()}</ModalFooter>
	) : (
		<div className={styles.popoverStyle}>{renderContent()}</div>
	);
};
